import { createContext, useState, useEffect, useContext, ReactNode, useCallback } from "react";
import { Factor } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { logger } from "@/lib/logger";

type AAL = "aal1" | "aal2";

interface MFAContextType {
  // Current AAL state
  currentAAL: AAL | null;
  nextRequiredAAL: AAL | null;

  // Enrolled factors
  enrolledFactors: Factor[];
  hasEnrolledTOTP: boolean;

  // Status flags
  needsMFAEnrollment: boolean;
  needsMFAVerification: boolean;
  isLoadingMFAStatus: boolean;

  // Actions
  refreshMFAStatus: () => Promise<void>;
}

const MFAContext = createContext<MFAContextType | undefined>(undefined);

export function MFAProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isAuthLoading } = useAuth();

  const [currentAAL, setCurrentAAL] = useState<AAL | null>(null);
  const [nextRequiredAAL, setNextRequiredAAL] = useState<AAL | null>(null);
  const [enrolledFactors, setEnrolledFactors] = useState<Factor[]>([]);
  const [isLoadingMFAStatus, setIsLoadingMFAStatus] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Derived state
  const hasEnrolledTOTP = enrolledFactors.some(
    (f) => f.factor_type === "totp" && f.status === "verified"
  );

  // User needs to enroll MFA if they have no verified TOTP factors
  const needsMFAEnrollment = user !== null && !hasEnrolledTOTP;

  // User needs to verify MFA if they have factors but current session is aal1
  const needsMFAVerification =
    user !== null &&
    hasEnrolledTOTP &&
    currentAAL === "aal1" &&
    nextRequiredAAL === "aal2";

  const refreshMFAStatus = useCallback(async () => {
    if (!user) {
      logger.debug("MFAContext: No user, resetting MFA state");
      setCurrentAAL(null);
      setNextRequiredAAL(null);
      setEnrolledFactors([]);
      setIsLoadingMFAStatus(false);
      setHasFetched(false);
      return;
    }

    logger.debug("MFAContext: Refreshing MFA status for user:", user.id);
    setIsLoadingMFAStatus(true);

    try {
      // Get AAL status
      const { data: aalData, error: aalError } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (aalError) {
        logger.error("MFAContext: Error getting AAL:", aalError);
        throw aalError;
      }

      logger.debug("MFAContext: AAL data:", aalData);
      setCurrentAAL(aalData.currentLevel as AAL);
      setNextRequiredAAL(aalData.nextLevel as AAL);

      // Get enrolled factors
      const { data: factorsData, error: factorsError } =
        await supabase.auth.mfa.listFactors();

      if (factorsError) {
        logger.error("MFAContext: Error listing factors:", factorsError);
        throw factorsError;
      }

      logger.debug("MFAContext: Factors data:", factorsData);

      // Combine all factor types
      const allFactors: Factor[] = [
        ...(factorsData.totp || []),
        ...(factorsData.phone || []),
      ];
      setEnrolledFactors(allFactors);

      logger.debug("MFAContext: MFA status refreshed", {
        currentAAL: aalData.currentLevel,
        nextAAL: aalData.nextLevel,
        factorCount: allFactors.length,
        hasVerifiedTOTP: allFactors.some(
          (f) => f.factor_type === "totp" && f.status === "verified"
        ),
      });
      setHasFetched(true);
    } catch (error) {
      logger.error("MFAContext: Failed to refresh MFA status:", error);
      // On error, assume no MFA enrolled to force enrollment
      setEnrolledFactors([]);
      setHasFetched(true);
    } finally {
      setIsLoadingMFAStatus(false);
    }
  }, [user]);

  // Use user.id as stable dependency to prevent infinite loops
  const userId = user?.id;

  // Refresh MFA status when user changes (using stable userId)
  useEffect(() => {
    // Don't fetch if auth is still loading
    if (isAuthLoading) {
      return;
    }

    // If no user, reset state
    if (!userId) {
      setCurrentAAL(null);
      setNextRequiredAAL(null);
      setEnrolledFactors([]);
      setIsLoadingMFAStatus(false);
      setHasFetched(false);
      return;
    }

    // Only fetch if we haven't already for this user
    if (!hasFetched) {
      refreshMFAStatus();
    }
  }, [userId, isAuthLoading, hasFetched, refreshMFAStatus]);

  const value: MFAContextType = {
    currentAAL,
    nextRequiredAAL,
    enrolledFactors,
    hasEnrolledTOTP,
    needsMFAEnrollment,
    needsMFAVerification,
    isLoadingMFAStatus,
    refreshMFAStatus,
  };

  return <MFAContext.Provider value={value}>{children}</MFAContext.Provider>;
}

export function useMFA() {
  const context = useContext(MFAContext);
  if (context === undefined) {
    throw new Error("useMFA must be used within an MFAProvider");
  }
  return context;
}
